// src/components/admin/teamTaskbox/TaskDetailModal.js
import React, { useState, useEffect } from 'react';
import {
    X, Edit, Save, Copy, Trash2, Plus, MessageSquare,
    Paperclip, Activity, Eye, CheckSquare, User, Calendar, Clock
} from 'lucide-react';
import teamTasksService from '../../../services/teamTasks.service';
import { useAuth } from '../../../hooks/useAuth';
import { useEmployees } from '../../../contexts/EmployeeContext';
import { useNotifications } from '../../../contexts/NotificationContext';

const TaskDetailModal = ({ taskId, onClose, onUpdate, onDelete, onClone }) => {
    const { user } = useAuth();
    const { employees } = useEmployees();
    const { addNotification } = useNotifications();
    const [task, setTask] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('overview');
    const [isEditing, setIsEditing] = useState(false);
    const [editedTask, setEditedTask] = useState({});

    // Sub-components state
    const [newSubtask, setNewSubtask] = useState({ title: '', description: '', assigned_employee_ids: [] });
    const [selectedSubtasks, setSelectedSubtasks] = useState([]);
    const [bulkStatusChange, setBulkStatusChange] = useState('completed');
    const [editingSubtask, setEditingSubtask] = useState(null);
    const [subtaskComments, setSubtaskComments] = useState({});
    const [newComment, setNewComment] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);
    const [attachmentDescription, setAttachmentDescription] = useState('');
    const [showMentionDropdown, setShowMentionDropdown] = useState(false);
    const [mentionSearch, setMentionSearch] = useState('');
    const [cursorPosition, setCursorPosition] = useState(0);

    useEffect(() => {
        fetchTaskDetails();
    }, [taskId]);

    // Close on Escape key
    useEffect(() => {
        const handleKeyDown = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);

    // Helper function to get employee name by ID
    const getEmployeeName = (employeeId) => {
        const employee = employees.find(emp => emp.id === employeeId);
        return employee ? `${employee.first_name} ${employee.last_name}` : 'Unknown Employee';
    };

    // Helper function to get employee details
    const getEmployeeDetails = (employeeId) => {
        return employees.find(emp => emp.id === employeeId);
    };

    // Extract assigned employee IDs - handle various data formats from backend
    const getAssignedEmployeeIds = (task) => {
        // Direct array (UUIDs are strings, keep them as strings)
        if (Array.isArray(task.assigned_employee_ids)) {
            return task.assigned_employee_ids.map(id => String(id).trim());
        }

        // Comma-separated string
        if (typeof task.assigned_employee_ids === 'string' && task.assigned_employee_ids.trim()) {
            return task.assigned_employee_ids.split(',').map(id => String(id).trim());
        }

        // Single value (UUID or ID)
        if (task.assigned_employee_ids) {
            return [String(task.assigned_employee_ids).trim()];
        }

        return [];
    };

    // Check if user can edit this task
    const canEditTask = (task) => {
        if (!task || !user) return false;
        return task.created_by === user.id || ['admin', 'Admin', 'Manager', 'manager'].includes(user.role);
    };

    // Check if user is an assignee of this task
    const isAssignee = (task) => {
        if (!task || !user) return false;
        const assignedIds = getAssignedEmployeeIds(task);
        return assignedIds.includes(user.id);
    };

    // Check if user can interact with this task (edit, comment, change subtask status)
    const canInteractWithTask = (task) => {
        if (!task || !user) return false;
        return canEditTask(task) || isAssignee(task);
    };

    // Check if user can change subtask status
    const canChangeSubtaskStatus = (task, subtask) => {
        if (!task || !user || !subtask) return false;

        // Admin/Manager can change any subtask
        if (canEditTask(task)) return true;

        // Assignees can change subtask status if they are assigned to that subtask
        if (isAssignee(task)) {
            const assignedIds = Array.isArray(subtask.assigned_employee_ids)
                ? subtask.assigned_employee_ids
                : (subtask.assigned_to_employee_id ? [subtask.assigned_to_employee_id] : []);
            return assignedIds.includes(user.id);
        }

        return false;
    };

    // Get eligible employees for @ mentions (admin, creator, assignees)
    const getEligibleMentionEmployees = () => {
        if (!task) return [];

        const eligibleIds = new Set();

        // Add admin users
        employees.forEach(emp => {
            if (['admin', 'Admin', 'Manager', 'manager'].includes(emp.role)) {
                eligibleIds.add(emp.id);
            }
        });

        // Add creator
        if (task.created_by) {
            eligibleIds.add(task.created_by);
        }

        // Add assigned employees
        getAssignedEmployeeIds(task).forEach(id => eligibleIds.add(id));

        // Return employee objects for eligible IDs
        return employees.filter(emp => eligibleIds.has(emp.id));
    };

    // Format date without timezone conversion
    const formatDateForDisplay = (dateString) => {
        if (!dateString) return 'No deadline';
        // Extract YYYY-MM-DD from ISO string
        const dateOnly = dateString.split('T')[0];
        const [year, month, day] = dateOnly.split('-');
        // Create date in local timezone
        const date = new Date(year, month - 1, day);
        return date.toLocaleDateString();
    };

    const fetchTaskDetails = async () => {
        try {
            setLoading(true);
            const response = await teamTasksService.getTeamTask(taskId);
            if (response.success) {
                console.log('🔍 TaskDetailModal - Fetched task data:', JSON.stringify(response.data, null, 2));
                console.log('🔍 TaskDetailModal - assigned_employee_ids:', response.data.assigned_employee_ids);
                console.log('🔍 TaskDetailModal - type of assigned_employee_ids:', typeof response.data.assigned_employee_ids);

                // Convert ISO date strings to yyyy-MM-dd format for date inputs
                const taskData = { ...response.data };
                if (taskData.deadline) {
                    taskData.deadline = taskData.deadline.split('T')[0];
                }
                if (taskData.due_date) {
                    taskData.due_date = taskData.due_date.split('T')[0];
                }

                setTask(response.data); // Keep original for display
                setEditedTask(taskData); // Use formatted for editing
            }
        } catch (error) {
            console.error('Error fetching task details:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleUpdate = async () => {
        try {
            // Check if trying to mark task as completed
            if (editedTask.status === 'completed') {
                // Count incomplete subtasks
                const incompleteSubtasks = task.subtasks?.filter(st => st.status !== 'completed') || [];

                if (incompleteSubtasks.length > 0) {
                    alert(`Cannot mark task as completed. ${incompleteSubtasks.length} subtask${incompleteSubtasks.length > 1 ? 's' : ''} still not completed.`);
                    return; // Prevent the update
                }
            }

            // Check if user is trying to complete task and has permission
            if (editedTask.status === 'completed' && !canEditTask(task)) {
                alert('Only task creators, admins, and managers can mark tasks as completed.');
                return;
            }

            // Prepare data for backend
            const assignedIds = getAssignedEmployeeIds(editedTask);
            const updateData = {
                ...editedTask,
                assigned_employee_ids: assignedIds
            };

            // Set assigned_to to first employee for compatibility
            if (assignedIds.length > 0) {
                updateData.assigned_to = assignedIds[0];
            }

            const response = await teamTasksService.updateTeamTask(taskId, updateData);
            if (response.success) {
                // Check if status changed
                const statusChanged = task.status !== editedTask.status;

                // Convert deadline for editing
                const taskData = { ...response.data };
                if (taskData.deadline) {
                    taskData.deadline = taskData.deadline.split('T')[0];
                }
                if (taskData.due_date) {
                    taskData.due_date = taskData.due_date.split('T')[0];
                }

                setTask(response.data);
                setEditedTask(taskData);
                setIsEditing(false);
                onUpdate();

                // Send notifications if status changed
                if (statusChanged) {
                    const assignedIds = getAssignedEmployeeIds(task);

                    // If task completed, notify manager/creator
                    if (editedTask.status === 'completed') {
                        // Notify the task creator (if not the person completing it)
                        if (task.created_by && task.created_by !== user.id) {
                            await addNotification({
                                userId: task.created_by,
                                type: 'task_completed',
                                title: 'Task Completed',
                                message: `${user.name || 'Team member'} has completed the task "${task.title}".`,
                                icon: 'CheckCircle',
                                color: 'green',
                                data: {
                                    task_id: taskId,
                                    task_title: task.title,
                                    completed_by: user.name
                                }
                            });
                        }

                        // Also notify as team_task_completed if creator is a manager
                        const creator = employees.find(emp => emp.id === task.created_by);
                        if (creator && ['Manager', 'manager', 'Admin', 'admin'].includes(creator.role)) {
                            await addNotification({
                                userId: task.created_by,
                                type: 'team_task_completed',
                                title: 'Team Task Completed',
                                message: `${user.name || 'Team member'} has completed the task "${task.title}". Due date was: ${task.due_date ? new Date(task.due_date).toLocaleDateString() : 'Not set'}`,
                                icon: 'CheckCircle',
                                color: 'green',
                                data: {
                                    task_id: taskId,
                                    task_title: task.title,
                                    employee_name: user.name,
                                    due_date: task.due_date
                                }
                            });
                        }
                    } else {
                        // Status changed to something other than completed
                        // Notify assigned employees (if updated by manager/creator)
                        if (canEditTask(task)) {
                            for (const employeeId of assignedIds) {
                                if (employeeId !== user.id) { // Don't notify the person making the change
                                    await addNotification({
                                        userId: employeeId,
                                        type: 'task_status_changed',
                                        title: 'Task Status Updated',
                                        message: `The status of task "${task.title}" has been changed to ${editedTask.status.replace('_', ' ')} by ${user.name || 'manager'}.`,
                                        icon: 'ClipboardCheck',
                                        color: 'blue',
                                        data: {
                                            task_id: taskId,
                                            task_title: task.title,
                                            new_status: editedTask.status,
                                            changed_by: user.name
                                        }
                                    });
                                }
                            }
                        }
                    }
                }

                // Show success message
                alert('Task updated successfully!');
            }
        } catch (error) {
            console.error('Error updating task:', error);
            alert('Failed to update task');
        }
    };

    const handleAddSubtask = async () => {
        if (!newSubtask.title.trim()) return;

        try {
            // Send multiple employee IDs - backend now supports this
            const subtaskData = {
                title: newSubtask.title,
                description: newSubtask.description,
                assigned_employee_ids: newSubtask.assigned_employee_ids,
                // Also send first ID for backward compatibility
                assigned_to_employee_id: newSubtask.assigned_employee_ids.length > 0 ? newSubtask.assigned_employee_ids[0] : null
            };

            const response = await teamTasksService.addSubTask(taskId, subtaskData);
            if (response.success) {
                fetchTaskDetails();
                setNewSubtask({ title: '', description: '', assigned_employee_ids: [] });
            }
        } catch (error) {
            console.error('Error adding subtask:', error);
        }
    };

    const handleUpdateSubtaskStatus = async (subtaskId, newStatus) => {
        try {
            const updateData = { status: newStatus };
            if (newStatus === 'completed') {
                updateData.is_completed = true;
            }
            await teamTasksService.updateSubTask(taskId, subtaskId, updateData);

            // After updating subtask, check if all subtasks are now completed
            setTimeout(async () => {
                try {
                    // Get fresh task details to check current subtask statuses
                    const response = await teamTasksService.getTeamTask(taskId);
                    if (response.success) {
                        const updatedTask = response.data;
                        const allSubtasks = updatedTask.subtasks || [];

                        // Check if all subtasks are completed
                        const allCompleted = allSubtasks.length > 0 &&
                            allSubtasks.every(st => st.status === 'completed');

                        // Check if all subtasks were completed and task status should change
                        if (allCompleted && updatedTask.status !== 'under_review' && updatedTask.status !== 'completed') {
                            // Auto-move task to under_review when all subtasks are completed
                            await teamTasksService.updateTeamTask(taskId, { status: 'under_review' });
                            alert('All subtasks completed! Task status automatically changed to "Under Review".');
                            fetchTaskDetails();
                        } else {
                            fetchTaskDetails();
                        }
                    } else {
                        fetchTaskDetails();
                    }
                } catch (error) {
                    console.error('Error checking subtask completion status:', error);
                    fetchTaskDetails(); // Fallback to regular refresh
                }
            }, 500); // Small delay to ensure backend has processed the update

        } catch (error) {
            console.error('Error updating subtask:', error);
        }
    };

    const handleBulkStatusChange = async () => {
        if (selectedSubtasks.length === 0) return;

        try {
            // Update all selected subtasks to the chosen status
            const updateData = { status: bulkStatusChange };
            if (bulkStatusChange === 'completed') {
                updateData.is_completed = true;
            }

            await Promise.all(
                selectedSubtasks.map(subtaskId =>
                    teamTasksService.updateSubTask(taskId, subtaskId, updateData)
                )
            );

            setSelectedSubtasks([]);

            // After updating subtasks, check if all subtasks are now completed
            setTimeout(async () => {
                try {
                    // Get fresh task details to check current subtask statuses
                    const response = await teamTasksService.getTeamTask(taskId);
                    if (response.success) {
                        const updatedTask = response.data;
                        const allSubtasks = updatedTask.subtasks || [];

                        // Check if all subtasks are completed
                        const allCompleted = allSubtasks.length > 0 &&
                            allSubtasks.every(st => st.status === 'completed');

                        // Check if all subtasks were completed and task status should change
                        if (allCompleted && updatedTask.status !== 'under_review' && updatedTask.status !== 'completed') {
                            // Auto-move task to under_review when all subtasks are completed
                            await teamTasksService.updateTeamTask(taskId, { status: 'under_review' });
                            alert('All subtasks completed! Task status automatically changed to "Under Review".');
                            fetchTaskDetails();
                        } else {
                            fetchTaskDetails();
                        }
                    } else {
                        fetchTaskDetails();
                    }
                } catch (error) {
                    console.error('Error checking subtask completion status:', error);
                    fetchTaskDetails(); // Fallback to regular refresh
                }
            }, 500); // Small delay to ensure backend has processed the updates

        } catch (error) {
            console.error('Error updating subtasks:', error);
        }
    };

    const handleEditSubtask = (subtask) => {
        // Handle both single employee ID and array of employee IDs
        let assignedIds = [];
        if (Array.isArray(subtask.assigned_employee_ids)) {
            assignedIds = subtask.assigned_employee_ids;
        } else if (subtask.assigned_to_employee_id) {
            assignedIds = [subtask.assigned_to_employee_id];
        }

        setEditingSubtask({
            id: subtask.id,
            title: subtask.title,
            description: subtask.description || '',
            assigned_employee_ids: assignedIds
        });
    };

    const handleUpdateSubtask = async () => {
        if (!editingSubtask) return;

        try {
            // Send multiple employee IDs - backend now supports this
            await teamTasksService.updateSubTask(taskId, editingSubtask.id, {
                title: editingSubtask.title,
                description: editingSubtask.description,
                assigned_employee_ids: editingSubtask.assigned_employee_ids,
                // Also send first ID for backward compatibility
                assigned_to_employee_id: editingSubtask.assigned_employee_ids.length > 0 ? editingSubtask.assigned_employee_ids[0] : null
            });

            setEditingSubtask(null);
            fetchTaskDetails();
        } catch (error) {
            console.error('Error updating subtask:', error);
        }
    };

    const handleAddSubtaskComment = async (subtaskId) => {
        const comment = subtaskComments[subtaskId];
        if (!comment || !comment.trim()) return;

        try {
            // Add comment to subtask (you may need to create a separate API endpoint for this)
            await teamTasksService.addComment(taskId, {
                comment_text: comment,
                subtask_id: subtaskId
            });

            setSubtaskComments({ ...subtaskComments, [subtaskId]: '' });
            fetchTaskDetails();
        } catch (error) {
            console.error('Error adding subtask comment:', error);
        }
    };

    const handleSubtaskSelection = (subtaskId) => {
        setSelectedSubtasks(prev =>
            prev.includes(subtaskId)
                ? prev.filter(id => id !== subtaskId)
                : [...prev, subtaskId]
        );
    };

    const handleCommentChange = (e) => {
        const text = e.target.value;
        const cursorPos = e.target.selectionStart;
        setNewComment(text);
        setCursorPosition(cursorPos);

        // Check if @ was just typed
        const textBeforeCursor = text.substring(0, cursorPos);
        const lastAtIndex = textBeforeCursor.lastIndexOf('@');

        if (lastAtIndex !== -1) {
            const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
            // Show dropdown if @ is followed by word characters or empty
            if (/^\w*$/.test(textAfterAt)) {
                setMentionSearch(textAfterAt.toLowerCase());
                setShowMentionDropdown(true);
            } else {
                setShowMentionDropdown(false);
            }
        } else {
            setShowMentionDropdown(false);
        }
    };

    const handleMentionSelect = (employee) => {
        const textBeforeCursor = newComment.substring(0, cursorPosition);
        const lastAtIndex = textBeforeCursor.lastIndexOf('@');
        const textAfterCursor = newComment.substring(cursorPosition);

        const newText = newComment.substring(0, lastAtIndex) + `@${employee.first_name}_${employee.last_name} ` + textAfterCursor;
        setNewComment(newText);
        setShowMentionDropdown(false);
        setMentionSearch('');
    };

    const handleAddComment = async () => {
        if (!newComment.trim()) return;

        try {
            // Parse mentions from comment text - extract employee IDs from @FirstName_LastName patterns
            const mentionPattern = /@(\w+)_(\w+)/g;
            const mentionedEmployeeIds = [];
            let match;

            while ((match = mentionPattern.exec(newComment)) !== null) {
                const firstName = match[1];
                const lastName = match[2];
                const employee = employees.find(emp =>
                    emp.first_name === firstName && emp.last_name === lastName
                );
                if (employee) {
                    mentionedEmployeeIds.push(employee.id);
                }
            }

            const response = await teamTasksService.addComment(taskId, {
                comment_text: newComment,
                mentions: mentionedEmployeeIds
            });
            if (response.success) {
                fetchTaskDetails();
                setNewComment('');
            }
        } catch (error) {
            console.error('Error adding comment:', error);
            alert('Failed to add comment. Please try again.');
        }
    };

    const handleFileUpload = async () => {
        if (!selectedFile) return;

        try {
            // Use the upload service from teamTasks.service
            const uploadResponse = await teamTasksService.uploadFile(selectedFile);

            if (uploadResponse.success) {
                const response = await teamTasksService.addAttachment(taskId, {
                    file_name: selectedFile.name,
                    file_url: uploadResponse.data?.downloadURL || uploadResponse.data?.url,
                    file_type: selectedFile.type,
                    file_size: selectedFile.size,
                    file_path: uploadResponse.data?.filePath,
                    description: attachmentDescription || null
                });

                if (response.success) {
                    fetchTaskDetails();
                    setSelectedFile(null);
                    setAttachmentDescription('');
                }
            }
        } catch (error) {
            console.error('Error uploading file:', error);
            alert('Failed to upload file. Please try again.');
        }
    };

    const handleAddWatcher = async (employeeId) => {
        try {
            await teamTasksService.addWatcher(taskId, employeeId);
            fetchTaskDetails();
        } catch (error) {
            console.error('Error adding watcher:', error);
        }
    };

    const tabs = [
        { id: 'overview', label: 'Overview', icon: Activity },
        { id: 'subtasks', label: 'Sub-tasks', icon: CheckSquare, count: task?.subtasks?.length },
        { id: 'comments', label: 'Comments', icon: MessageSquare, count: task?.comments?.length },
        { id: 'attachments', label: 'Attachments', icon: Paperclip, count: task?.attachments?.length },
        { id: 'activity', label: 'Activity', icon: Activity },
        { id: 'watchers', label: 'Watchers', icon: Eye, count: task?.watchers?.length }
    ];

    if (loading) {
        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white dark:bg-gray-800 rounded-lg p-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                </div>
            </div>
        );
    }

    if (!task) {
        return null;
    }

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex-1">
                        {isEditing ? (
                            <input
                                value={editedTask.title}
                                onChange={(e) => setEditedTask({ ...editedTask, title: e.target.value })}
                                className="text-2xl font-bold w-full px-2 py-1 border border-blue-500 rounded dark:bg-gray-700 dark:text-white"
                            />
                        ) : (
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{task.title}</h2>
                        )}
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            Created by {task.created_by_name} • {new Date(task.created_at).toLocaleDateString()}
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        {isEditing ? (
                            <>
                                <button
                                    onClick={handleUpdate}
                                    className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                                    title="Save changes"
                                >
                                    <Save className="w-5 h-5" />
                                </button>
                                <button
                                    onClick={() => setIsEditing(false)}
                                    className="p-2 bg-gray-200 dark:bg-gray-700 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
                                >
                                    Cancel
                                </button>
                            </>
                        ) : (
                            <>
                                {canEditTask(task) && (
                                    <>
                                        <button
                                            onClick={() => setIsEditing(true)}
                                            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                                            title="Edit task"
                                        >
                                            <Edit className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                                        </button>
                                        <button
                                            onClick={onClone}
                                            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                                            title="Clone task"
                                        >
                                            <Copy className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                                        </button>
                                        <button
                                            onClick={onDelete}
                                            className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg"
                                            title="Delete task"
                                        >
                                            <Trash2 className="w-5 h-5 text-red-600 dark:text-red-400" />
                                        </button>
                                    </>
                                )}
                            </>
                        )}
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                        >
                            <X className="w-6 h-6 text-gray-600 dark:text-gray-400" />
                        </button>
                    </div>
                </div>

                {/* Tabs */}
                <div className="flex border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
                    {tabs.map(tab => {
                        const Icon = tab.icon;
                        return (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`flex items-center gap-2 px-6 py-3 border-b-2 transition-colors whitespace-nowrap ${
                                    activeTab === tab.id
                                        ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                                        : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                                }`}
                            >
                                <Icon className="w-4 h-4" />
                                <span>{tab.label}</span>
                                {tab.count > 0 && (
                                    <span className="px-2 py-0.5 text-xs bg-gray-200 dark:bg-gray-700 rounded-full">
                                        {tab.count}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>

                {/* Tab Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    {/* Overview Tab */}
                    {activeTab === 'overview' && (
                        <div className="space-y-6">
                            <div>
                                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Description</h3>
                                {isEditing ? (
                                    <textarea
                                        value={editedTask.description || ''}
                                        onChange={(e) => setEditedTask({ ...editedTask, description: e.target.value })}
                                        rows={4}
                                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                                    />
                                ) : (
                                    <p className="text-gray-900 dark:text-white">{task.description || 'No description'}</p>
                                )}
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Status</h3>
                                    {isEditing ? (
                                        <select
                                            value={editedTask.status}
                                            onChange={(e) => setEditedTask({ ...editedTask, status: e.target.value })}
                                            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                                        >
                                            <option value="pending">Pending</option>
                                            <option value="in_progress">In Progress</option>
                                            {canEditTask(task) && <option value="under_review">Under Review</option>}
                                            {/* Only show completed option if user has permission and all subtasks are completed */}
                                            {canEditTask(task) && (!task.subtasks || task.subtasks.length === 0 || task.subtasks.every(st => st.status === 'completed')) && (
                                                <option value="completed">Completed</option>
                                            )}
                                            <option value="on_hold">On Hold</option>
                                        </select>
                                    ) : (
                                        <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
                                            {task.status?.replace('_', ' ').toUpperCase()}
                                        </span>
                                    )}
                                </div>

                                <div>
                                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Priority</h3>
                                    {isEditing ? (
                                        <select
                                            value={editedTask.priority}
                                            onChange={(e) => setEditedTask({ ...editedTask, priority: e.target.value })}
                                            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                                        >
                                            <option value="immediate">Immediate</option>
                                            <option value="urgent">Urgent</option>
                                            <option value="next_in_line">Next in Line</option>
                                            <option value="scheduled">Scheduled</option>
                                            <option value="deferred">Deferred</option>
                                        </select>
                                    ) : (
                                        <span className="text-gray-900 dark:text-white">{task.priority}</span>
                                    )}
                                </div>

                                <div>
                                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Deadline</h3>
                                    {isEditing ? (
                                        <input
                                            type="date"
                                            value={editedTask.deadline || ''}
                                            onChange={(e) => setEditedTask({ ...editedTask, deadline: e.target.value })}
                                            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 dark:text-white"
                                        />
                                    ) : (
                                        <div className="flex items-center gap-2 text-gray-900 dark:text-white">
                                            <Calendar className="w-4 h-4" />
                                            {formatDateForDisplay(task.deadline)}
                                        </div>
                                    )}
                                </div>

                                <div>
                                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Estimated Hours</h3>
                                    {isEditing ? (
                                        <input
                                            type="number"
                                            value={editedTask.estimated_hours || ''}
                                            onChange={(e) => setEditedTask({ ...editedTask, estimated_hours: e.target.value })}
                                            placeholder="Enter estimated hours"
                                            min="0"
                                            step="0.5"
                                            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 dark:text-white"
                                        />
                                    ) : (
                                        <div className="flex items-center gap-2 text-gray-900 dark:text-white">
                                            <Clock className="w-4 h-4" />
                                            {task.estimated_hours ? `${task.estimated_hours}h` : 'Not set'}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div>
                                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Assigned Employees</h3>
                                {isEditing ? (
                                    <div className="space-y-2">
                                        {employees.map(emp => (
                                            <label key={emp.id} className="flex items-center gap-2 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
                                                <input
                                                    type="checkbox"
                                                    checked={getAssignedEmployeeIds(editedTask).includes(emp.id) || false}
                                                    onChange={(e) => {
                                                        const currentIds = getAssignedEmployeeIds(editedTask);
                                                        const updatedIds = e.target.checked
                                                            ? [...currentIds, emp.id]
                                                            : currentIds.filter(id => id !== emp.id);
                                                        // Store the updated assigned employee IDs in the edited task
                                                        setEditedTask({
                                                            ...editedTask,
                                                            assigned_employee_ids: updatedIds
                                                        });
                                                    }}
                                                    className="w-4 h-4 text-blue-600"
                                                />
                                                {emp.profile_picture_url && (
                                                    <img src={emp.profile_picture_url} alt={emp.first_name} className="w-6 h-6 rounded-full" />
                                                )}
                                                <span className="text-sm text-gray-900 dark:text-white">
                                                    {emp.first_name} {emp.last_name}
                                                </span>
                                                {emp.designation && (
                                                    <span className="text-xs text-gray-500">({emp.designation})</span>
                                                )}
                                            </label>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="flex flex-wrap gap-2">
                                        {getAssignedEmployeeIds(task).map(empId => {
                                            const emp = getEmployeeDetails(empId);
                                            return emp ? (
                                                <div key={empId} className="flex items-center gap-2 px-3 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                                                    {emp.profile_picture_url && (
                                                        <img src={emp.profile_picture_url} alt={emp.first_name} className="w-6 h-6 rounded-full" />
                                                    )}
                                                    <span className="text-sm text-gray-900 dark:text-white">
                                                        {emp.first_name} {emp.last_name}
                                                    </span>
                                                </div>
                                            ) : null;
                                        })}
                                        {getAssignedEmployeeIds(task).length === 0 && (
                                            <span className="text-gray-500 italic">No employees assigned</span>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Subtask Status Summary */}
                            {task.subtasks && task.subtasks.length > 0 && (
                                <div>
                                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Sub-tasks Summary</h3>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                        <div className="p-3 bg-gray-100 dark:bg-gray-700 rounded-lg">
                                            <div className="text-2xl font-bold text-gray-900 dark:text-white">
                                                {task.subtasks.filter(st => st.status === 'pending').length}
                                            </div>
                                            <div className="text-xs text-gray-600 dark:text-gray-400">Pending</div>
                                        </div>
                                        <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                                            <div className="text-2xl font-bold text-blue-700 dark:text-blue-400">
                                                {task.subtasks.filter(st => st.status === 'in_progress').length}
                                            </div>
                                            <div className="text-xs text-blue-600 dark:text-blue-400">In Progress</div>
                                        </div>
                                        <div className="p-3 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                                            <div className="text-2xl font-bold text-orange-700 dark:text-orange-400">
                                                {task.subtasks.filter(st => st.status === 'on_hold').length}
                                            </div>
                                            <div className="text-xs text-orange-600 dark:text-orange-400">On Hold</div>
                                        </div>
                                        <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
                                            <div className="text-2xl font-bold text-green-700 dark:text-green-400">
                                                {task.subtasks.filter(st => st.status === 'completed').length}
                                            </div>
                                            <div className="text-xs text-green-600 dark:text-green-400">Completed</div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Sub-tasks Tab */}
                    {activeTab === 'subtasks' && (
                        <div className="space-y-4">
                            {/* Bulk Action Bar */}
                            {selectedSubtasks.length > 0 && (
                                <div className="flex items-center justify-between p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                                    <span className="text-sm text-blue-700 dark:text-blue-300">
                                        {selectedSubtasks.length} subtask{selectedSubtasks.length > 1 ? 's' : ''} selected
                                    </span>
                                    <div className="flex items-center gap-2">
                                        <select
                                            value={bulkStatusChange}
                                            onChange={(e) => setBulkStatusChange(e.target.value)}
                                            className="px-3 py-2 border border-blue-300 dark:border-blue-700 rounded-lg dark:bg-gray-700 dark:text-white"
                                        >
                                            <option value="pending">Pending</option>
                                            <option value="in_progress">In Progress</option>
                                            <option value="on_hold">On Hold</option>
                                            <option value="completed">Completed</option>
                                        </select>
                                        <button
                                            onClick={handleBulkStatusChange}
                                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                                        >
                                            <CheckSquare className="w-4 h-4" />
                                            Update Status
                                        </button>
                                    </div>
                                </div>
                            )}

                            {task.subtasks?.map(subtask => {
                                const statusColors = {
                                    pending: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
                                    in_progress: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
                                    on_hold: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
                                    completed: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                };

                                const isEditing = editingSubtask?.id === subtask.id;

                                return (
                                    <div key={subtask.id} className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg space-y-3">
                                        <div className="flex items-start gap-3">
                                            {/* Selection Checkbox */}
                                            <input
                                                type="checkbox"
                                                checked={selectedSubtasks.includes(subtask.id)}
                                                onChange={() => handleSubtaskSelection(subtask.id)}
                                                className="mt-1 w-4 h-4 text-blue-600 rounded border-gray-300 dark:border-gray-600"
                                            />

                                            <div className="flex-1">
                                                <div className="flex items-start justify-between gap-2 mb-2">
                                                    {isEditing ? (
                                                        <input
                                                            type="text"
                                                            value={editingSubtask.title}
                                                            onChange={(e) => setEditingSubtask({ ...editingSubtask, title: e.target.value })}
                                                            className="flex-1 px-2 py-1 border border-blue-500 rounded dark:bg-gray-600 dark:text-white"
                                                        />
                                                    ) : (
                                                        <h4 className={`font-medium flex-1 ${subtask.status === 'completed' ? 'line-through text-gray-500' : 'text-gray-900 dark:text-white'}`}>
                                                            {subtask.title}
                                                        </h4>
                                                    )}
                                                    {/* Color-coded Status Label */}
                                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[subtask.status] || statusColors.pending}`}>
                                                        {subtask.status.replace('_', ' ').toUpperCase()}
                                                    </span>
                                                </div>

                                                {isEditing ? (
                                                    <textarea
                                                        value={editingSubtask.description}
                                                        onChange={(e) => setEditingSubtask({ ...editingSubtask, description: e.target.value })}
                                                        placeholder="Description..."
                                                        rows={2}
                                                        className="w-full px-2 py-1 border border-blue-500 rounded dark:bg-gray-600 dark:text-white text-sm mb-2"
                                                    />
                                                ) : (
                                                    subtask.description && (
                                                        <p className={`text-sm mt-1 ${subtask.status === 'completed' ? 'line-through text-gray-400' : 'text-gray-600 dark:text-gray-400'}`}>
                                                            {subtask.description}
                                                        </p>
                                                    )
                                                )}

                                                {isEditing ? (
                                                    <div className="space-y-1 border border-blue-500 rounded p-2 max-h-32 overflow-y-auto dark:bg-gray-600">
                                                        {getAssignedEmployeeIds(task).map(empId => {
                                                            const emp = getEmployeeDetails(empId);
                                                            return emp ? (
                                                                <label key={empId} className="flex items-center gap-2 p-1 hover:bg-gray-100 dark:hover:bg-gray-500 rounded cursor-pointer">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={editingSubtask.assigned_employee_ids.includes(empId)}
                                                                        onChange={(e) => {
                                                                            const updatedIds = e.target.checked
                                                                                ? [...editingSubtask.assigned_employee_ids, empId]
                                                                                : editingSubtask.assigned_employee_ids.filter(id => id !== empId);
                                                                            setEditingSubtask({ ...editingSubtask, assigned_employee_ids: updatedIds });
                                                                        }}
                                                                        className="w-4 h-4 text-blue-600"
                                                                    />
                                                                    <span className="text-sm text-gray-900 dark:text-white">
                                                                        {emp.first_name} {emp.last_name}
                                                                    </span>
                                                                </label>
                                                            ) : null;
                                                        })}
                                                    </div>
                                                ) : (
                                                    // Display all assigned employees
                                                    (subtask.assigned_employee_ids && subtask.assigned_employee_ids.length > 0) ? (
                                                        <div className="flex flex-wrap gap-1 mt-2">
                                                            {subtask.assigned_employee_ids.map(empId => {
                                                                const emp = getEmployeeDetails(empId);
                                                                return emp ? (
                                                                    <div key={empId} className="flex items-center gap-1 px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded text-xs">
                                                                        <User className="w-3 h-3" />
                                                                        {emp.first_name} {emp.last_name}
                                                                    </div>
                                                                ) : null;
                                                            })}
                                                        </div>
                                                    ) : subtask.assigned_to_employee_id ? (
                                                        <div className="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400 mt-2">
                                                            <User className="w-3 h-3" />
                                                            {getEmployeeName(subtask.assigned_to_employee_id)}
                                                        </div>
                                                    ) : null
                                                )}
                                            </div>

                                            {/* Action Buttons */}
                                            <div className="flex items-center gap-2">
                                                {isEditing ? (
                                                    <>
                                                        <button
                                                            onClick={handleUpdateSubtask}
                                                            className="p-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                                                        >
                                                            <Save className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            onClick={() => setEditingSubtask(null)}
                                                            className="p-1 bg-gray-200 dark:bg-gray-600 rounded hover:bg-gray-300"
                                                        >
                                                            <X className="w-4 h-4" />
                                                        </button>
                                                    </>
                                                ) : (
                                                    <>
                                                        {canEditTask(task) && (
                                                            <button
                                                                onClick={() => handleEditSubtask(subtask)}
                                                                className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                                                                title="Edit subtask"
                                                            >
                                                                <Edit className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                                                            </button>
                                                        )}
                                                        {canChangeSubtaskStatus(task, subtask) ? (
                                                            <select
                                                                value={subtask.status}
                                                                onChange={(e) => handleUpdateSubtaskStatus(subtask.id, e.target.value)}
                                                                className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-600"
                                                            >
                                                                <option value="pending">Pending</option>
                                                                <option value="in_progress">In Progress</option>
                                                                <option value="on_hold">On Hold</option>
                                                                <option value="completed">Completed</option>
                                                            </select>
                                                        ) : (
                                                            <span className="px-2 py-1 text-xs border border-gray-200 dark:border-gray-600 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                                                                {subtask.status.replace('_', ' ').toUpperCase()}
                                                            </span>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        </div>

                                        {/* Comment Section */}
                                        <div className="ml-7 mt-2">
                                            <div className="flex gap-2">
                                                <input
                                                    type="text"
                                                    value={subtaskComments[subtask.id] || ''}
                                                    onChange={(e) => setSubtaskComments({ ...subtaskComments, [subtask.id]: e.target.value })}
                                                    placeholder="Add a comment (optional)..."
                                                    className="flex-1 px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-600 dark:text-white"
                                                />
                                                <button
                                                    onClick={() => handleAddSubtaskComment(subtask.id)}
                                                    disabled={!subtaskComments[subtask.id]?.trim()}
                                                    className="px-3 py-1 text-sm bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-white rounded hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    <MessageSquare className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}

                            {/* Add New Subtask */}
                            {canEditTask(task) && (
                                <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                    <input
                                        type="text"
                                        value={newSubtask.title}
                                        onChange={(e) => setNewSubtask({ ...newSubtask, title: e.target.value })}
                                        placeholder="New sub-task title..."
                                        className="w-full px-4 py-2 mb-2 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-600 dark:text-white"
                                    />
                                    <textarea
                                        value={newSubtask.description || ''}
                                        onChange={(e) => setNewSubtask({ ...newSubtask, description: e.target.value })}
                                        placeholder="Description (optional)..."
                                        rows={2}
                                        className="w-full px-4 py-2 mb-2 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-600 dark:text-white"
                                    />

                                    {/* Assign Multiple Employees */}
                                    <div className="mb-2">
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Assign to Employees (from assigned list)
                                        </label>
                                        <div className="max-h-40 overflow-y-auto space-y-1 border border-gray-300 dark:border-gray-600 rounded p-2">
                                            {getAssignedEmployeeIds(task).map(empId => {
                                                const emp = getEmployeeDetails(empId);
                                                return emp ? (
                                                    <label key={empId} className="flex items-center gap-2 p-1 hover:bg-gray-100 dark:hover:bg-gray-600 rounded">
                                                        <input
                                                            type="checkbox"
                                                            checked={newSubtask.assigned_employee_ids.includes(empId)}
                                                            onChange={(e) => {
                                                                const updatedIds = e.target.checked
                                                                    ? [...newSubtask.assigned_employee_ids, empId]
                                                                    : newSubtask.assigned_employee_ids.filter(id => id !== empId);
                                                                setNewSubtask({ ...newSubtask, assigned_employee_ids: updatedIds });
                                                            }}
                                                            className="w-4 h-4 text-blue-600"
                                                        />
                                                        <span className="text-sm text-gray-900 dark:text-white">
                                                            {emp.first_name} {emp.last_name}
                                                        </span>
                                                    </label>
                                                ) : null;
                                            })}
                                        </div>
                                    </div>

                                    <button
                                        onClick={handleAddSubtask}
                                        className="w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center justify-center gap-2"
                                    >
                                        <Plus className="w-4 h-4" />
                                        Add Sub-task
                                    </button>
                                </div>
                            )}

                            {!canEditTask(task) && isAssignee(task) && (
                                <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                    <p className="text-center text-gray-600 dark:text-gray-400">
                                        Only task creators and managers can add new subtasks.
                                    </p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Comments Tab */}
                    {activeTab === 'comments' && (
                        <div className="space-y-4">
                            {task.comments?.map(comment => {
                                // Find subtask if this is a subtask comment
                                const subtask = comment.subtask_id
                                    ? task.subtasks?.find(st => st.id === comment.subtask_id)
                                    : null;

                                return (
                                    <div key={comment.id} className="flex gap-3">
                                        {comment.employee_avatar && (
                                            <img src={comment.employee_avatar} alt={comment.employee_name} className="w-10 h-10 rounded-full" />
                                        )}
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-1">
                                                <span className="font-medium text-gray-900 dark:text-white">{comment.employee_name}</span>
                                                <span className="text-xs text-gray-500">{new Date(comment.created_at).toLocaleString()}</span>
                                                {subtask && (
                                                    <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 text-xs rounded-full">
                                                        Subtask: {subtask.title}
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-gray-700 dark:text-gray-300">{comment.comment}</p>
                                        </div>
                                    </div>
                                );
                            })}

                            {/* Add Comment */}
                            <div className="mt-4 relative">
                                <textarea
                                    value={newComment}
                                    onChange={handleCommentChange}
                                    placeholder="Add a comment... Use @ to mention someone"
                                    rows={3}
                                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 dark:text-white"
                                />

                                {/* Mention Dropdown */}
                                {showMentionDropdown && (
                                    <div className="absolute z-10 mt-1 w-full max-h-48 overflow-y-auto bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg">
                                        {getEligibleMentionEmployees()
                                            .filter(emp =>
                                                mentionSearch === '' ||
                                                emp.first_name.toLowerCase().includes(mentionSearch) ||
                                                emp.last_name.toLowerCase().includes(mentionSearch)
                                            )
                                            .map(emp => (
                                                <div
                                                    key={emp.id}
                                                    onClick={() => handleMentionSelect(emp)}
                                                    className="px-4 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 flex items-center gap-2"
                                                >
                                                    {emp.profile_picture_url && (
                                                        <img src={emp.profile_picture_url} alt={emp.first_name} className="w-6 h-6 rounded-full" />
                                                    )}
                                                    <div>
                                                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                                                            {emp.first_name} {emp.last_name}
                                                        </div>
                                                        {emp.designation && (
                                                            <div className="text-xs text-gray-500">{emp.designation}</div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                    </div>
                                )}

                                {canInteractWithTask(task) && (
                                    <button
                                        onClick={handleAddComment}
                                        disabled={!newComment.trim()}
                                        className="mt-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Comment
                                    </button>
                                )}

                                {!canInteractWithTask(task) && (
                                    <div className="mt-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                                        <p className="text-sm text-yellow-800 dark:text-yellow-200">
                                            You don't have permission to comment on this task. Only task creators, managers, and assigned employees can comment.
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Attachments Tab */}
                    {activeTab === 'attachments' && (
                        <div className="space-y-4">
                            {task.attachments?.map(attachment => (
                                <div key={attachment.id} className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                    <div className="flex items-start gap-3">
                                        <Paperclip className="w-5 h-5 text-gray-600 flex-shrink-0 mt-1" />
                                        <div className="flex-1">
                                            <a href={attachment.file_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-medium">
                                                {attachment.file_name}
                                            </a>
                                            <p className="text-xs text-gray-500 mt-1">
                                                Uploaded by {attachment.uploaded_by_name} • {new Date(attachment.uploaded_at).toLocaleDateString()}
                                            </p>
                                            {attachment.description && (
                                                <p className="text-sm text-gray-700 dark:text-gray-300 mt-2 p-2 bg-white dark:bg-gray-600 rounded border border-gray-200 dark:border-gray-600">
                                                    {attachment.description}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}

                            {/* Upload File */}
                            <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg space-y-3">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Select File
                                    </label>
                                    <input
                                        type="file"
                                        onChange={(e) => setSelectedFile(e.target.files[0])}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-600 dark:text-white"
                                    />
                                </div>
                                {selectedFile && (
                                    <>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Description (Optional)
                                            </label>
                                            <textarea
                                                value={attachmentDescription}
                                                onChange={(e) => setAttachmentDescription(e.target.value)}
                                                placeholder="Add a description or comment about this file..."
                                                rows={2}
                                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-600 dark:text-white"
                                            />
                                        </div>
                                        {canInteractWithTask(task) && (
                                            <button
                                                onClick={handleFileUpload}
                                                className="w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center justify-center gap-2"
                                            >
                                                <Paperclip className="w-4 h-4" />
                                                Upload Attachment
                                            </button>
                                        )}

                                        {!canInteractWithTask(task) && (
                                            <div className="w-full p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                                                <p className="text-sm text-yellow-800 dark:text-yellow-200 text-center">
                                                    You don't have permission to upload attachments to this task.
                                                </p>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Activity Tab */}
                    {activeTab === 'activity' && (
                        <div className="space-y-4">
                            {task.activity_log?.map(activity => (
                                <div key={activity.id} className="flex gap-3">
                                    <div className="flex-shrink-0 w-2 h-2 mt-2 bg-blue-600 rounded-full"></div>
                                    <div className="flex-1">
                                        <p className="text-sm text-gray-900 dark:text-white">
                                            <span className="font-medium">{activity.employee_name}</span> {activity.activity_description}
                                        </p>
                                        <p className="text-xs text-gray-500">{new Date(activity.created_at).toLocaleString()}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Watchers Tab */}
                    {activeTab === 'watchers' && (
                        <div className="space-y-4">
                            {task.watchers && task.watchers.length > 0 ? (
                                task.watchers.map(watcher => {
                                    // Handle both object format and employee ID format
                                    const watcherId = watcher.employee_id || watcher.id;
                                    const watcherEmployee = getEmployeeDetails(watcherId);

                                    return (
                                        <div key={watcher.id || watcherId} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                            <div className="flex items-center gap-3">
                                                {watcherEmployee?.profile_picture_url && (
                                                    <img
                                                        src={watcherEmployee.profile_picture_url}
                                                        alt={watcherEmployee.first_name}
                                                        className="w-10 h-10 rounded-full"
                                                    />
                                                )}
                                                <div>
                                                    <p className="font-medium text-gray-900 dark:text-white">
                                                        {watcher.employee_name ||
                                                         (watcherEmployee ? `${watcherEmployee.first_name} ${watcherEmployee.last_name}` : 'Unknown')}
                                                    </p>
                                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                                        {watcher.employee_email || watcherEmployee?.email || 'No email'}
                                                    </p>
                                                    {watcher.added_at && (
                                                        <p className="text-xs text-gray-500 mt-1">
                                                            Watching since {new Date(watcher.added_at).toLocaleDateString()}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })
                            ) : (
                                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                                    <Eye className="w-12 h-12 mx-auto mb-2 opacity-50" />
                                    <p>No watchers yet</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TaskDetailModal;
